import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import IProjectGroupsCompletionItem from "../dataTypes/IProjectGroupsCompletionItem";

interface EnduserProgressionCardProps {
  parcours: IProjectGroupsCompletionItem[];
}

/**
 * Hero card displaying overall progression across all parcours
 */
const EnduserProgressionCard = ({ parcours }: EnduserProgressionCardProps) => {
  const { t } = useTranslation();

  const { completed, total, percentage, getProgressColor, getProgressTextColor } = useMemo(() => {
    const completedCount = parcours.filter(
      (p) => Math.round(p.progressPercentageOnLatestBuild) === 100,
    ).length;
    const totalCount = parcours.length;
    const pct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    // Always green - progress is always positive and encouraging!
    const getColor = (): string => "bg-green-500";
    const getTextColor = (): string => "text-green-600";

    return {
      completed: completedCount,
      total: totalCount,
      percentage: pct,
      getProgressColor: getColor,
      getProgressTextColor: getTextColor,
    };
  }, [parcours]);

  // Calculate in-progress count
  const inProgressCount = useMemo(() => {
    return parcours.filter((p) => {
      const progress = Math.round(p.progressPercentageOnLatestBuild);
      return progress > 0 && progress < 100;
    }).length;
  }, [parcours]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">
          {t("enduserStats.heroCards.progression")}
        </h3>
        <div className="p-2 bg-green-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      </div>

      {/* Main progression display */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`text-3xl font-bold ${getProgressTextColor()}`}>
            {percentage.toFixed(0)}%
          </span>
          <span className="text-sm text-gray-500">
            {t("enduserStats.heroCards.progressionSubtitle")}
          </span>
        </div>

        {/* Visual gauge */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${getProgressColor()} transition-all duration-500 ease-out rounded-full`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Parcours count */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl font-bold text-gray-900">{completed}</span>
        <span className="text-gray-500">/ {total}</span>
        <span className="text-sm text-gray-500">
          {t("enduserStats.heroCards.parcoursCompleted")}
        </span>
      </div>

      {/* Secondary metrics */}
      <div className="flex items-center mt-auto pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-yellow-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs text-gray-500">{t("enduserStats.heroCards.inProgress")}</span>
          <span className="text-sm font-semibold text-gray-900">{inProgressCount}</span>
        </div>
      </div>
    </div>
  );
};

export default EnduserProgressionCard;
